/**
 * Admin page: list all workspaces with member/transaction counts.
 * Access: allowlist only (backend ADMIN_UIDS). No link in app; open URL /admin.
 */

import { useState, useEffect, useCallback, useRef, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BarChartIcon from "@mui/icons-material/BarChart";
import { auth } from "../firebase";
import { api } from "../services/api";
import { getDemoWorkspaceId } from "../utils/demoMode";

type AdminRow = {
  id: string;
  name: string;
  ownerId: string;
  ownerEmail?: string;
  type?: string;
  memberCount: number;
  transactionCount: number;
  firebaseReads?: number;
  firebaseWrites?: number;
  firebaseCostUsd?: number;
  storageBytesUsed?: number;
};

type AdminDeletedRow = AdminRow & { deletedAt: string };

function formatStorageBytes(bytes: number | undefined): string {
  if (bytes == null || bytes === 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

type UsageInterval = "current_month";

type UsageData = {
  interval: { label: string; start: string; end: string };
  totalReads: number;
  totalWrites: number;
  estimatedCostUsd: number;
  firebasePricing: { readsPer100k: number; writesPer100k: number };
  actualCostUsd: number | null;
  actualCostBreakdown?: Array<{ serviceId: string; serviceDescription: string; cost: number }>;
  /** True when BigQuery export is configured but has no data for this month yet. */
  billingExportPending?: boolean;
  /** When actual cost is null: 'not_configured' | 'no_data_yet' | 'error'. */
  billingStatus?: "not_configured" | "no_data_yet" | "error";
} | null;

export default function AdminPage(): ReactElement {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminRow[]>([]);
  const [deletedRows, setDeletedRows] = useState<AdminDeletedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageInterval, setUsageInterval] = useState<UsageInterval>("current_month");
  const [usageData, setUsageData] = useState<UsageData>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const fetchUsage = useCallback(async (): Promise<void> => {
    setUsageLoading(true);
    try {
      const data = await api.getAdminUsage(usageInterval);
      setUsageData(data);
    } catch (err) {
      if (err instanceof Error && err.message !== "FORBIDDEN") {
        console.warn("Admin usage fetch failed:", err);
      }
      setUsageData(null);
    } finally {
      setUsageLoading(false);
    }
  }, [usageInterval]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAdminWorkspaces();
      setRows(data.active);
      setDeletedRows(data.deleted);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      if (message === "FORBIDDEN") {
        setRows([]);
        setDeletedRows([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /** Sync: send current active IDs so backend can move any that no longer exist in Firebase to the deleted list. */
  const handleSync = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const previousActiveIds = rows.map((r) => r.id);
      const data = await api.syncAdminWorkspaces(previousActiveIds);
      setRows(data.active);
      setDeletedRows(data.deleted);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      if (message === "FORBIDDEN") {
        setRows([]);
        setDeletedRows([]);
      }
    } finally {
      setLoading(false);
    }
  }, [rows]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isForbidden = error === "FORBIDDEN";

  /*
   * ADMIN PAGE SCROLL FIX (keep for later reference)
   * -----------------------------------------------
   * Problem: When the mouse hovers over the Active workspaces or Deleted workspaces table cards,
   * the page scroll did not respond to the wheel. Same for any card that contains a table/list/data.
   * Cause: MUI TableContainer (and the wrapper with overflow-x: auto) creates a scroll context that
   * captures wheel events, so the browser never scrolls the page.
   * Solution: We handle wheel in capture phase (onWheelCapture) on the page scroll container,
   * apply the scroll ourselves with preventDefault, and use velocity + damping so it feels like
   * native scroll (smooth acceleration/deceleration). Tuning: MAX_VELOCITY, WHEEL_STRENGTH, DAMPING.
   * Do not remove this handler without fixing the root cause (e.g. table not capturing wheel).
   */
  const MAX_VELOCITY = 28;
  const WHEEL_STRENGTH = 0.14;
  const DAMPING = 0.88;
  const STOP_THRESHOLD = 0.3;
  const wheelRef = useRef({
    velocity: 0,
    scrollable: null as HTMLElement | null,
    rafId: 0,
  });
  const tick = useCallback(() => {
    const w = wheelRef.current;
    const el = w.scrollable;
    if (!el) {
      w.rafId = 0;
      return;
    }
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 0) {
      w.velocity = 0;
      w.rafId = 0;
      return;
    }
    el.scrollTop += w.velocity;
    el.scrollTop = Math.max(0, Math.min(el.scrollTop, maxScroll));
    w.velocity *= DAMPING;
    if (Math.abs(w.velocity) < STOP_THRESHOLD) w.velocity = 0;
    if (w.velocity !== 0) {
      w.rafId = requestAnimationFrame(tick);
    } else {
      w.rafId = 0;
    }
  }, []);
  const handlePageWheel = useCallback(
    (e: React.WheelEvent<HTMLElement>) => {
      const scrollable = e.currentTarget;
      const style = window.getComputedStyle(scrollable);
      if (style.overflowY !== "auto" && style.overflowY !== "scroll") return;
      const lineHeight = 24;
      const delta =
        e.deltaMode === 0 ? e.deltaY : e.deltaMode === 1 ? e.deltaY * lineHeight : e.deltaY * scrollable.clientHeight;
      e.preventDefault();
      e.stopPropagation();
      const w = wheelRef.current;
      w.scrollable = scrollable;
      w.velocity += delta * WHEEL_STRENGTH;
      w.velocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, w.velocity));
      if (w.rafId === 0) {
        w.rafId = requestAnimationFrame(tick);
      }
    },
    [tick]
  );

  // Access denied (403) — early return
  if (isForbidden) {
    return (
      <Box sx={{ height: "100vh", overflowY: "auto", overflowX: "hidden", bgcolor: "background.default", py: 3, px: 2 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/")} sx={{ textTransform: "none", mb: 2 }}>
            Back to app
          </Button>
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/", { replace: true })}>
                Back to app
              </Button>
            }
          >
            Access denied. Your account is not in the admin allowlist. Add your Firebase UID to{" "}
            <strong>ADMIN_UIDS</strong> in the backend <code>.env</code> and restart the server.
            {auth.currentUser && (
              <Typography component="span" display="block" sx={{ mt: 1, fontFamily: "monospace", fontSize: "0.85rem" }}>
                Your UID: <strong>{auth.currentUser.uid}</strong>
              </Typography>
            )}
          </Alert>
        </Box>
      </Box>
    );
  }

  // Other errors (timeout, network) — early return
  if (error) {
    return (
      <Box sx={{ height: "100vh", overflowY: "auto", overflowX: "hidden", bgcolor: "background.default", py: 3, px: 2 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/")} sx={{ textTransform: "none", mb: 2 }}>
            Back to app
          </Button>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchData} title="Refresh list">
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  // Loading state (initial load only)
  if (loading && rows.length === 0) {
    return (
      <Box sx={{ height: "100vh", overflowY: "auto", overflowX: "hidden", bgcolor: "background.default", py: 3, px: 2 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/")} sx={{ textTransform: "none", mb: 2 }}>
            Back to app
          </Button>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography color="text.secondary">Loading workspaces…</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: "background.default",
        py: 3,
        px: 2,
      }}
      onWheelCapture={handlePageWheel}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/")}
              sx={{ textTransform: "none" }}
            >
              Back to app
            </Button>
            <Chip
              icon={<AdminPanelSettingsIcon />}
              label="Admin"
              color="primary"
              variant="outlined"
              size="small"
            />
          </Stack>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="h5" fontWeight={600}>
              Workspaces
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                handleSync();
                fetchUsage();
              }}
              disabled={loading}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              title="Sync: workspaces that no longer exist in Firebase are moved to the Deleted list"
            >
              {loading ? "Syncing…" : "Sync"}
            </Button>
          </Stack>
        </Stack>

        {/* Usage / Dashboard — interval and aggregated usage */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "action.hover",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <BarChartIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              Usage
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="admin-usage-interval-label">Interval</InputLabel>
              <Select
                labelId="admin-usage-interval-label"
                value={usageInterval}
                label="Interval"
                onChange={(e) => setUsageInterval(e.target.value as UsageInterval)}
              >
                <MenuItem value="current_month">Current month</MenuItem>
              </Select>
            </FormControl>
            {usageLoading ? (
              <CircularProgress size={24} />
            ) : usageData ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {usageData.interval.label} ({usageData.interval.start} – {usageData.interval.end})
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ alignItems: "center" }}>
                  <Typography variant="body2">
                    <strong>Reads:</strong> {usageData.totalReads.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Writes:</strong> {usageData.totalWrites.toLocaleString()}
                  </Typography>
                </Stack>
                <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Estimated cost (USD):</strong> ${usageData.estimatedCostUsd.toFixed(4)} — based on
                    Firestore list pricing: ${usageData.firebasePricing.readsPer100k}/100K reads, $
                    {usageData.firebasePricing.writesPer100k}/100K writes (us-central1).
                  </Typography>
                  <Typography variant="body2">
                    <strong>Actual cost (USD):</strong>{" "}
                    {usageData.actualCostUsd != null ? (
                      `$${usageData.actualCostUsd.toFixed(4)}`
                    ) : usageData.billingStatus === "no_data_yet" || usageData.billingExportPending ? (
                      "— BigQuery connected; data for this month not yet available (billing export often delayed 24–48 h)."
                    ) : usageData.billingStatus === "error" ? (
                      "— BigQuery env vars set but query failed (check backend logs; verify table name and BigQuery Data Viewer permission)."
                    ) : (
                      "— Set BILLING_BIGQUERY_PROJECT, BILLING_BIGQUERY_DATASET, and BILLING_BIGQUERY_TABLE (or BILLING_ACCOUNT_ID) in backend .env, or FIREBASE_ACTUAL_COST_USD, or see Cloud Console → Billing"
                    )}
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                    <strong>By service (Firestore, Storage, etc.):</strong>{" "}
                    {usageData.actualCostBreakdown && usageData.actualCostBreakdown.length > 0 ? (
                      usageData.actualCostBreakdown.map((s, i) => (
                        <span key={s.serviceId || i}>
                          {i > 0 ? "; " : ""}
                          {s.serviceDescription || s.serviceId || "Other"}: ${s.cost.toFixed(2)}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: "var(--mui-palette-text-secondary)" }}>
                        — Shown when Actual cost is from BigQuery
                      </span>
                    )}
                  </Typography>
                </Stack>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No usage data for this interval.
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Reads/writes are aggregated from app-tracked usage. Estimated cost uses Firebase list pricing. Actual cost
            is from BigQuery when <code>BILLING_BIGQUERY_PROJECT</code>, <code>BILLING_BIGQUERY_DATASET</code>, and{" "}
            <code>BILLING_BIGQUERY_TABLE</code> (or <code>BILLING_ACCOUNT_ID</code>) are set. The table can take a few
            hours to appear; data for the current month often appears 24–48 h later.
          </Typography>
        </Paper>

        {/* How to access — always visible so it's clear why it might not work */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "action.hover",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            How to access this page
          </Typography>
          <Stack component="ol" spacing={0.5} sx={{ m: 0, pl: 2.5, "& li": { typography: "body2" } }}>
            <li>
              Open the URL <strong>/admin</strong> (e.g.{" "}
              <code style={{ background: "rgba(0,0,0,0.06)", padding: "2px 6px", borderRadius: 4 }}>
                https://yoursite.com/admin
              </code>
              ).
            </li>
            <li>
              You must be <strong>logged in</strong>. If not, you’ll be sent to the login page, then back here after
              signing in.
            </li>
            <li>
              Your Firebase user ID must be in the backend <strong>ADMIN_UIDS</strong> (comma‑separated in the backend{" "}
              <code>.env</code>). Otherwise you’ll see “Access denied”.
            </li>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            There is no link to this page in the app; only direct URL or bookmark.
          </Typography>
        </Paper>

        {/* Active workspaces table */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Active workspaces
        </Typography>
        <Box sx={{ overflowX: "auto", overflowY: "hidden", mb: 3 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ overflow: "visible", minWidth: 0 }}>
            <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>ID</TableCell>
                <TableCell title="Workspace type: Company or Personal (set in workspace settings if available)">Type</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>Owner</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell align="right">Reads</TableCell>
                <TableCell align="right">Writes</TableCell>
                <TableCell align="right">Cost (USD)</TableCell>
                <TableCell align="right">Storage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No workspaces found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{row.name || "—"}</span>
                        {row.id === getDemoWorkspaceId() && (
                          <Chip size="small" label="App demo" color="primary" variant="outlined" title="This is the workspace used by the app's Try demo (unauthenticated) flow" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{row.id}</TableCell>
                    <TableCell>{row.type ?? "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {row.ownerEmail || row.ownerId || "—"}
                    </TableCell>
                    <TableCell align="right">{row.memberCount}</TableCell>
                    <TableCell align="right">{row.transactionCount}</TableCell>
                    <TableCell align="right">{row.firebaseReads ?? "—"}</TableCell>
                    <TableCell align="right">{row.firebaseWrites ?? "—"}</TableCell>
                    <TableCell align="right">{row.firebaseCostUsd ?? "—"}</TableCell>
                    <TableCell align="right">{formatStorageBytes(row.storageBytesUsed)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>

        {/* Deleted workspaces table */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Deleted workspaces
        </Typography>
        <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
          <TableContainer component={Paper} variant="outlined" sx={{ overflow: "visible", minWidth: 0 }}>
            <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>Owner</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell>Deleted at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deletedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No deleted workspaces. Deletions from the app are listed here.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                deletedRows.map((row) => (
                  <TableRow key={row.id} sx={{ bgcolor: "action.hover" }}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <span>{row.name || "—"}</span>
                        <Chip size="small" label="Deleted" color="default" variant="outlined" />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{row.id}</TableCell>
                    <TableCell>{row.type ?? "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {row.ownerEmail || row.ownerId || "—"}
                    </TableCell>
                    <TableCell align="right">{row.memberCount}</TableCell>
                    <TableCell align="right">{row.transactionCount}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {row.deletedAt ? new Date(row.deletedAt).toLocaleString() : "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}